/**
 * A positioning component wrapper that positions the given `hoverElement` relatively to its wrapped children
 * Used by components like DropdownButton and Tooltip.
 */
import uniqueId from 'lodash.uniqueid';
import React from 'react';

import { getComponentClasses } from '../../utils/app';
import { HoverCard } from '../HoverCard';

export type Type = 'tooltip' | 'dropdown';
export type Position =
    | 'up-left'
    | 'up-center'
    | 'up-right'
    | 'down-left'
    | 'down-center'
    | 'down-right'
    | 'left-top'
    | 'left-middle'
    | 'left-bottom'
    | 'right-top'
    | 'right-middle'
    | 'right-bottom'
    | 'up-fluid'
    | 'down-fluid';

export interface Props {
    alignmentOffset?: number;
    hasTransition?: boolean;
    hoverElement: React.ReactNode;
    hoverPosition?: Position;
    isActive?: boolean;
    type?: Type;
    onHoverElementShow?: () => void;
    onHoverElementHide?: () => void;
    variations?: string[];
    hoverCardVariations?: [string];
    children?: React.ReactNode;
}

export const HoverableElement = ({
    alignmentOffset = 0,
    hasTransition = false,
    hoverElement,
    hoverPosition = 'down-center',
    isActive = false,
    type = 'tooltip',
    onHoverElementShow,
    onHoverElementHide,
    variations = [],
    hoverCardVariations = [''],
    children,
}: Props) => {
    const id = uniqueId('hoverable_');
    return (
        <span className={getComponentClasses('hover-card-anchor', variations)} id={id} suppressHydrationWarning={true}>
            {children}
            <HoverCard
                anchorNodeSelector={`#${id}`}
                alignmentOffset={alignmentOffset}
                hasTransition={hasTransition}
                hasAnimation={false}
                hoverPosition={hoverPosition}
                isActive={isActive}
                type={type}
                onShow={onHoverElementShow}
                onHide={onHoverElementHide}
                variations={hoverCardVariations}
            >
                {hoverElement}
            </HoverCard>
        </span>
    );
};

export default HoverableElement;
